/**
 * POI proximity watcher — fires progress `poi_visited` events when the
 * user comes within a threshold distance of a Tscheppaschlucht POI.
 *
 * Data source: artrack `/tracks/30/waypoints/detail?limit=1000` — same
 * bulk-load NavigatePage / EntdeckenPage already use. We deliberately
 * fetch it independently so the watcher works on ANY page (including
 * /chat where the user just wants to talk, no map open).
 *
 * Position source: LocationService singleton. We subscribe passively —
 * we don't call `startTracking()` ourselves. GPS is off until a page
 * with a legitimate reason to ask for it (NavigatePage, /chat host)
 * turns it on. That keeps the permission prompt out of the entry flow.
 *
 * Dedupe strategy:
 *   - Client-side session dedupe: each POI fires at most once per
 *     watcher-lifetime (in-memory Set). No point spamming events
 *     while the user stands still 5m from a POI for a minute.
 *   - Server-side dedupe: even if the same client_event_id somehow
 *     hits twice, the progress backend returns deduped=true. Our
 *     tracker layer generates fresh UUIDs per fire though, so this
 *     is a belt-and-braces safety net.
 *
 * Trigger threshold: 30m airline distance (Haversine). Chosen to be
 * larger than typical GPS jitter on-trail (5-15m) but small enough
 * that a POI on the OTHER side of the gorge doesn't false-fire when
 * the user walks past on the opposite bank.
 */

import { getLocationService } from '../../domain/LocationService'
import { haversine } from '../../domain/geo'
import { trackEvent } from './progressTracker'
import { getConfig } from '../../config/project'

const TRIGGER_DISTANCE_M = 30
const POI_BULK_LIMIT = 1000

interface WaypointRow {
  id: number
  name: string
  latitude: number
  longitude: number
  category?: string | null
}

interface CachedPoi {
  waypoint_id: number
  name: string
  lat: number
  lng: number
}

let pois: CachedPoi[] = []
let firedThisSession = new Set<number>()
let stopFn: (() => void) | null = null
let bootstrapped = false

/** Load POIs once per app-session. Idempotent. */
async function loadPois(): Promise<void> {
  if (pois.length > 0) return
  try {
    // Tenant-aware via getConfig() — was hardcoded to Tscheppa track 30 +
    // master key, which 403'd against the isolated udo tenant (smoke
    // 2026-07-15). Header was once missing entirely → 401 (Tschepp).
    const { artrackUrl, trackId, apiKey } = getConfig().api
    const res = await fetch(
      `${artrackUrl}/tracks/${trackId}/waypoints/detail?limit=${POI_BULK_LIMIT}`,
      { headers: { 'X-API-KEY': apiKey } },
    )
    if (!res.ok) {
      console.warn('[poiWatcher] artrack fetch failed:', res.status)
      return
    }
    const rows = (await res.json()) as WaypointRow[] | { waypoints?: WaypointRow[] }
    const list: WaypointRow[] = Array.isArray(rows) ? rows : (rows.waypoints ?? [])
    // Title lives in different fields depending on how the waypoint was
    // created: `name` (Tscheppa imports), top-level `title` (slim shape) or
    // `metadata_json.title` (manual/provisioned waypoints, e.g. the udo
    // stations — those came back filtered to 0 before this fallback chain).
    const titleOf = (w: WaypointRow & { title?: string; metadata_json?: { title?: string } }) =>
      w.name ?? w.title ?? w.metadata_json?.title
    pois = list
      .filter((w) =>
        typeof w.id === 'number' &&
        typeof w.latitude === 'number' &&
        typeof w.longitude === 'number' &&
        typeof titleOf(w) === 'string',
      )
      .map((w) => ({
        waypoint_id: w.id,
        name: titleOf(w) as string,
        lat: w.latitude,
        lng: w.longitude,
      }))
    console.log(`[poiWatcher] loaded ${pois.length} POIs`)
  } catch (e) {
    console.warn('[poiWatcher] artrack fetch error:', e)
  }
}

function onPosition(lat: number, lng: number): void {
  if (pois.length === 0) return
  for (const poi of pois) {
    if (firedThisSession.has(poi.waypoint_id)) continue
    const d = haversine(lat, lng, poi.lat, poi.lng)
    if (d <= TRIGGER_DISTANCE_M) {
      firedThisSession.add(poi.waypoint_id)
      trackEvent({
        kind: 'poi_visited',
        waypoint_id: poi.waypoint_id,
        label: poi.name,
        payload: { distance_m: Math.round(d), trigger: `gps<${TRIGGER_DISTANCE_M}m` },
      })
      console.log(`[poiWatcher] fired poi_visited: ${poi.name} @ ${Math.round(d)}m`)
    }
  }
}

/**
 * Start the watcher. Idempotent — subsequent calls are no-ops.
 * Returns a stop function (unwires the LocationService listener).
 *
 * The caller doesn't need to worry about GPS-permission or tracking —
 * we passively subscribe. If GPS is off, positions never arrive and
 * the watcher is silent (correctly). When some other page enables
 * tracking, we start ticking.
 */
export function startPoiWatcher(): () => void {
  if (bootstrapped) return () => { /* already running */ }
  bootstrapped = true
  void loadPois()
  const ls = getLocationService()
  stopFn = ls.onPositionChanged((pos) => {
    onPosition(pos.raw.lat, pos.raw.lng)
  })
  return () => {
    if (stopFn) stopFn()
    stopFn = null
    bootstrapped = false
    firedThisSession = new Set()
  }
}
